/**
 * Every tunable number in one place, as the spec requires. Changing a value
 * here changes it everywhere; nothing below is duplicated in feature code.
 */

/** The only course that exists today. Stored on every record so others can be
 *  added later without a migration. There is deliberately no course UI. */
export const DEFAULT_COURSE = 'CHE1045';

/** How often a running app looks for a newer build. */
export const UPDATE_CHECK_MS = 10 * 60 * 1000;

/** Stamped in at build time, so a screen can say which build it is. */
export const APP_BUILD = __APP_BUILD__;

export const IMPORT_FORMAT = 'chem-bank-import';
export const IMPORT_VERSION = 1;

/** Leitner boxes 1..5 and how long each waits before coming due again.
 *  Box 1 is "next session", modelled as immediately due but not repeated
 *  within the run it was just missed in. */
export const LEITNER_BOXES = [1, 2, 3, 4, 5] as const;
export const LEITNER_INTERVAL_DAYS: Record<number, number> = {
  1: 0,
  2: 1,
  3: 3,
  4: 7,
  5: 14,
};
export const MAX_BOX = 5;
export const MIN_BOX = 1;

/** How many recent results each progress record keeps. */
export const RECENT_RESULTS_KEPT = 8;

/** Difficulty ramp. Auto climbs at >= UP, drops at <= DOWN, judged over the
 *  last WINDOW answers at the current level. */
export const RAMP_WINDOW = 8;
export const RAMP_UP_THRESHOLD = 0.8;
export const RAMP_DOWN_THRESHOLD = 0.4;

/** Default draw sizes. */
export const CUMULATIVE_CARD_COUNT = 40;
export const CUMULATIVE_QUIZ_COUNT = 20;

/** Cumulative de-duplication: word-set overlap at or above this counts as a
 *  duplicate, and the chapter-review copy is the one kept. */
export const DEDUPE_OVERLAP = 0.85;

/** A category needs this many attempts before its accuracy is trustworthy. */
export const LOW_CONFIDENCE_ATTEMPTS = 5;

/** Raw attempts older than this are rolled up into per-category per-day totals. */
export const ATTEMPT_RETENTION_DAYS = 90;

export interface ExamPreset {
  id: string;
  label: string;
  questions: number;
  minutes: number;
}

export const EXAM_PRESETS: ExamPreset[] = [
  { id: 'quick10', label: 'Quick 10', questions: 10, minutes: 15 },
  { id: 'half', label: 'Half exam', questions: 20, minutes: 45 },
  { id: 'full', label: 'Full exam', questions: 40, minutes: 90 },
];

/**
 * Library.
 *
 * Netlify Blobs allows 5 GB per object, but a browser cannot write to Blobs
 * directly — every write goes through a function, and a synchronous function
 * caps its request at 6 MB (about 4.5 MB once base64-encoded). Presigned
 * uploads to object storage are not on offer either. So files travel as raw
 * binary chunks, and the per-request limit stops being the file-size limit.
 *
 * 3 MB chunks leave a wide margin under 6 MB. Downloads are chunked the same
 * way, because a synchronous response is capped at 6 MB too.
 */
export const LIBRARY_FILE_CAP_BYTES = 25 * 1024 * 1024;
export const UPLOAD_CHUNK_BYTES = 3 * 1024 * 1024;
export const UPLOAD_CHUNK_RETRIES = 3;
export const IMAGE_LONG_EDGE_PX = 2000;
export const IMAGE_JPEG_QUALITY = 0.85;

/**
 * What the Library will take.
 *
 * PDFs and images are shown as they are. Slide decks and documents are shown
 * as their text — see src/library/documents.ts for why that is the line. The
 * pre-2007 binaries (.ppt, .doc) and .rtf are stored and synced but cannot be
 * read in a browser, and the viewer says so rather than failing quietly.
 */
export const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/heic',
  'image/heif',

  // Slide decks and documents.
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.oasis.opendocument.presentation',
  'application/vnd.oasis.opendocument.text',
  'application/rtf',
  'text/plain',
  'text/markdown',
  'text/csv',
] as const;

export const REFUSED_FILE_MESSAGE =
  'The Library takes PDFs, images, slide decks (.pptx, .ppt, .odp) and documents (.docx, .doc, .odt, .rtf, .txt, .md, .csv). Export this to PDF first, then add it.';

/** Sync. Batches stay well under the Netlify synchronous function payload
 *  limit (~6 MB); these are records-per-push and a byte ceiling. */
export const SYNC_PUSH_MAX_RECORDS = 400;
export const SYNC_PUSH_MAX_BYTES = 1_500_000;
export const SYNC_DEBOUNCE_MS = 2500;
export const TOKEN_TTL_DAYS = 90;

/** Eight category colours, assigned by hashing the category name. Light and
 *  dark variants are paired so a category keeps its identity in both themes. */
export const CATEGORY_COLORS = [
  { light: '#2E6DA0', dark: '#6FB3E0' },
  { light: '#2F9E5B', dark: '#5FCB8B' },
  { light: '#B4632A', dark: '#E0965C' },
  { light: '#7A4FA3', dark: '#B48BD8' },
  { light: '#1F8A8A', dark: '#5FCACA' },
  { light: '#A8443C', dark: '#E0837B' },
  { light: '#5A6B2F', dark: '#A9BE6B' },
  { light: '#8A4C74', dark: '#CE8DB4' },
] as const;

/** A saved place in a card deck or a quiz is picked up again for this long.
 *  Past it the run is stale — the spacing has moved on — and a fresh draw is
 *  the better answer than resuming a fortnight-old deck. */
export const RESUME_MAX_AGE_DAYS = 14;

/** Whiteboard. Strokes are stored normalised, so the cap is on how much ink
 *  one board holds rather than on pixels; past it the oldest strokes go. */
export const WHITEBOARD_MAX_STROKES = 400;
export const WHITEBOARD_PEN_WIDTHS = [2, 4, 8] as const;

/** How much of the screen a docked whiteboard may take, as a fraction. */
export const DOCK_MIN_SIZE = 0.25;
export const DOCK_MAX_SIZE = 0.7;

/** Calculator history kept on screen, newest first. */
export const CALC_HISTORY_KEPT = 8;

/** The same list as file-name endings, for pickers that do not take types.
 *  Both are sent: Android maps types reliably and extensions badly, and some
 *  desktop pickers do the reverse. */
export const ACCEPTED_EXTENSIONS = [
  '.pdf',
  '.png',
  '.jpg',
  '.jpeg',
  '.webp',
  '.heic',
  '.heif',
  '.pptx',
  '.ppt',
  '.docx',
  '.doc',
  '.odp',
  '.odt',
  '.rtf',
  '.txt',
  '.md',
  '.csv',
] as const;
